import React, { useMemo } from 'react';
import {
  MaterialReactTable,
  type MRT_ColumnDef,
  MRT_GlobalFilterTextField,
  useMaterialReactTable
} from 'material-react-table';
import { Box, Button, lighten, Stack } from '@mui/material';
import { RemoveRedEye } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

type StudentHistory = {
  year: number;
  value: number;
};

type StudentData = {
  student: {
    id: number;
    nome: string;
  };
  history: StudentHistory[];
  situation: string;
  bolsista?: boolean;
};

type Person = {
  name: string;
  inadimplencia: { [year: number]: number };
  alunoSituacao: string;
  bolsista?: boolean;
};

const TableHeaderRecomendacao = ({ data }: { data: StudentData[] }) => {
  const navigate = useNavigate();

  // Memoize transformed data and years to avoid recomputing on every render
  const transformedData: Person[] = useMemo(() => {
    return data.map(item => ({
      name: item.student.nome,
      bolsista: item?.bolsista,
      inadimplencia: item.history.reduce((acc, curr) => {
        acc[curr.year] = curr.value;
        return acc;
      }, {} as { [year: number]: number }),
      alunoSituacao: item.situation === 'high' ? 'Alto índice de inadimplência' :
        item.situation === 'medium' ? 'Médio índice de inadimplência' :
          'Baixo índice de inadimplência',
    }));
  }, [data]);

  const years = useMemo(() => {
    // Obtendo anos únicos e ordenando em ordem crescente
    return Array.from(new Set(data.flatMap(item => item.history.map(h => h.year)))).sort((a, b) => a - b);
  }, [data]);

  const columns: Array<MRT_ColumnDef<Person>> = useMemo(() => [
    {
      accessorKey: 'name',
      header: 'Aluno',
      sortDescFirst: true,
      enableMultiSort: true,
      Cell: ({ renderedCellValue }) => (
        <div style={{ marginLeft: '20px' }}>{renderedCellValue}</div>
      ),
    },
    ...years.map(year => ({
      accessorKey: `inadimplencia.${year}`,
      header: `Inadimplência ${year}`,
      Cell: ({ row }) => (
        <div style={{ marginLeft: '20px' }}>{row.getValue(`inadimplencia.${year}`)} %</div>
      ),
    })),
    {
      accessorKey: 'alunoSituacao',
      header: 'Situação aluno',
      Cell: ({ row }) => {
        console.log(row.original);
        const { bolsista, alunoSituacao } = row.original;
        const text = bolsista ? 'Bolsista' : alunoSituacao;
        const bg = bolsista
          ? 'yellow'
          : alunoSituacao === 'Alto índice de inadimplência'
            ? '#FCE4D4'
            : alunoSituacao === 'Médio índice de inadimplência'
              ? '#FEF8CB'
              : 'rgba(206, 250, 206, 1)';

        return (
          <Box
            sx={{
              backgroundColor: bg,
              height: '60px',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {text}
          </Box>
        );
      },
    },
  ], [years]);

  const tableBasic = useMaterialReactTable({
    columns,
    data: transformedData,
    enableGlobalFilter: true,
    positionActionsColumn: 'last',
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableGrouping: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableColumnFilters: false,
    enableHiding: false,
    enableExpandAll: false,
    enableRowDragging: false,
    enableColumnActions: false,
    enableRowActions: true,
    enableRowSelection: false,
    enableBottomToolbar: false,
    localization: {
      search: 'Pesquisar por nome do aluno',
      actions: 'Ações', 
    },
    initialState: {
      showGlobalFilter: true,
    },
    paginationDisplayMode: 'default',
    enablePagination: false,
    positionToolbarAlertBanner: 'bottom',
    muiSearchTextFieldProps: {
      variant: 'outlined',
      size: 'small',
      fullWidth: true,          
      placeholder: 'Pesquisar por nome…',
    },
    muiPaginationProps: {
      color: 'secondary',
      rowsPerPageOptions: [10, 20, 30],
      shape: 'rounded',
      variant: 'outlined',
    },
    renderRowActions: ({ row }) => {
      const studentId = data[row.index].student.id;
      const studentName = data[row.index].student.nome;

      return (
        <Stack alignItems={'center'}>
          <RemoveRedEye
            sx={{
              cursor: 'pointer',
            }}
            onClick={() => navigate('/inadimplencia-recomendacoes-alunos', {
              state: {
                id: studentId,
                name: studentName,
              }
            })}
          />
        </Stack>
      );
    },
    muiTableHeadCellProps: ({ column }) => ({
      sx: {
        backgroundColor: '#F7F7F7',
        color: 'black',
        fontWeight: 400,
        fontSize: '12px',
      },
    }),
    muiTableBodyCellProps: () => ({
      sx: {
        backgroundColor: '#fff',
        color: 'rgba(128, 128, 128, 1)',
        fontWeight: 400,
        fontSize: '12px',
        padding: '0',
      },
    }),
    renderTopToolbar: ({ table }) => (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        p={1}
      >
        {/* LEFT‑aligned, full width filter */}
        <Box flexGrow={6} mr={2}>
          <MRT_GlobalFilterTextField
            table={table}
            //@ts-ignore
            textFieldProps={{
              placeholder: 'Pesquisar por nome do aluno…',
              variant: 'outlined',
              size: 'small',
              fullWidth: true,
            }}
          />
        </Box>

        {/* if you have any right‑side buttons, put them here */}
        {/* <Button onClick={…}>My Action</Button> */}
      </Box>
    )
  });

  return (
    <>
      <MaterialReactTable table={tableBasic} />
    </>
  );
};

export default React.memo(TableHeaderRecomendacao);